'use client';

/**
 * LinkConnectionModal Component
 *
 * Modal for linking an organization connection to a project.
 * Shows available connections that are not yet linked to this project.
 *
 * @example
 * ```tsx
 * <LinkConnectionModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   projectId="proj-123"
 *   organizationId="org-456"
 *   linkedConnectionIds={['conn-789']}
 *   onLinked={() => refetch()}
 * />
 * ```
 */

import {
  Alert,
  AlertDescription,
  Button,
  Center,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { GitBranch, Link2Off } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

// =============================================================================
// Types
// =============================================================================

interface Connection {
  id: string;
  label: string;
  provider_id: string;
  provider_type: string;
  validation_status: string | null;
  metadata: {
    github_user?: { login: string; avatar_url: string };
  };
}

interface LinkConnectionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Project ID to link connections to */
  projectId: string;
  /** Organization ID to fetch connections from */
  organizationId: string;
  /** IDs of connections already linked to this project */
  linkedConnectionIds: string[];
  /** Callback when a connection is successfully linked */
  onLinked?: () => void;
}

// =============================================================================
// Helpers
// =============================================================================

function getProviderIcon(providerId: string) {
  switch (providerId.toLowerCase()) {
    case 'github':
      return <GitBranch className="h-5 w-5" />;
    default:
      return <Link2Off className="h-5 w-5" />;
  }
}

// =============================================================================
// Component
// =============================================================================

export function LinkConnectionModal({
  open,
  onClose,
  projectId,
  organizationId,
  linkedConnectionIds,
  onLinked,
}: LinkConnectionModalProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [connections, setConnections] = React.useState<Connection[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Selected connection state
  const [selectedConnection, setSelectedConnection] =
    React.useState<Connection | null>(null);

  // Linking state
  const [linking, setLinking] = React.useState(false);

  // Fetch available connections when modal opens
  React.useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      setSelectedConnection(null);
      return;
    }

    async function fetchConnections() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetchWithAuth(
          `/admin/organizations/${organizationId}/connections`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch connections');
        }

        const { data } = await response.json();
        // Filter out already-linked connections
        const linkedSet = new Set(linkedConnectionIds);
        const available = (data?.connections || []).filter(
          (c: Connection) =>
            !linkedSet.has(c.id) && c.validation_status === 'valid'
        );
        setConnections(available);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load connections'
        );
      } finally {
        setLoading(false);
      }
    }

    fetchConnections();
  }, [open, organizationId, linkedConnectionIds, fetchWithAuth]);

  // Handle connection selection
  const handleSelectConnection = (connection: Connection) => {
    setSelectedConnection(connection);
  };

  // Handle linking the connection
  const handleLink = async () => {
    if (!selectedConnection) return;

    setLinking(true);
    setError(null);

    try {
      // Link the connection to the project
      const response = await fetchWithAuth(
        `/admin/connections/${selectedConnection.id}/projects`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to link connection');
      }

      onLinked?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to link connection'
      );
    } finally {
      setLinking(false);
    }
  };

  // Check if we can proceed with linking
  const canLink = selectedConnection !== null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Link Connection</DialogTitle>
          <DialogDescription>
            Select an organization connection to link to this project
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <Center minHeight="8rem">
                <Spinner size="md" variant="muted" />
              </Center>
            ) : connections.length === 0 ? (
              <Stack gap="sm" align="center" className="py-8 text-center">
                <Link2Off className="text-foreground/60 mb-3 h-10 w-10" />
                <Text weight="medium" className="mb-1">
                  No available connections
                </Text>
                <Text color="muted" size="sm">
                  All organization connections are already linked to this
                  project, or no valid connections exist.
                </Text>
              </Stack>
            ) : (
              <Stack gap="xs">
                <Text weight="medium" size="sm" className="mb-2">
                  Select Connection
                </Text>
                <Stack gap="xs">
                  {connections.map((connection) => {
                    const isSelected = selectedConnection?.id === connection.id;

                    return (
                      <Button
                        key={connection.id}
                        type="button"
                        variant="ghost"
                        onClick={() => handleSelectConnection(connection)}
                        className={`h-auto w-full rounded-lg border p-3 text-left transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border bg-surface-1 hover:bg-surface-2'
                        }`}
                      >
                        <Inline gap="sm">
                          <Center className="bg-surface-2 h-10 w-10 rounded-lg">
                            {getProviderIcon(connection.provider_id)}
                          </Center>
                          <Stack className="flex-1" gap="none">
                            <Text weight="medium" size="sm">
                              {connection.label}
                            </Text>
                            <Text color="muted" size="xs">
                              {connection.provider_id}
                              {connection.metadata.github_user &&
                                ` • @${connection.metadata.github_user.login}`}
                            </Text>
                          </Stack>
                          {isSelected && (
                            <Stack className="bg-primary h-2 w-2 rounded-full" />
                          )}
                        </Inline>
                      </Button>
                    );
                  })}
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={linking}>
            Cancel
          </Button>
          <Button onClick={handleLink} disabled={!canLink} loading={linking}>
            Link Connection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
